import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, toImg } from "@/api/client";
import PropertyCard from "@/components/PropertyCard";
import ContactForm from "@/components/ContactForm";
import SEO, { realEstateAgentSchema } from "@/components/SEO";
import TrustBadge from "@/components/TrustBadge";
import { CONTACT } from "@/config/contact";
import { ArrowRight, Home as HomeIcon, TrendingUp, HeartHandshake, Sparkles, Quote, Star, MapPin } from "lucide-react";

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [zones, setZones] = useState([]);
  const [posts, setPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/properties", { params: { featured: true, limit: 6 } }).then((r) => setProperties(r.data)).catch(() => {});
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
    api.get("/blog", { params: { limit: 3 } }).then((r) => setPosts(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      <SEO
        title="Zélia Silva — Consultora imobiliária no Porto e Grande Porto"
        description="Comprar ou vender casa no Porto, Gondomar, Maia, Ermesinde e Valongo com a Zélia Silva. 10+ anos de experiência, avaliação gratuita e acompanhamento humano."
        canonical="https://casanoporto.com/"
        jsonLd={realEstateAgentSchema()}
      />

      {/* HERO */}
      {settings.hero_video_url && (
        <section className="relative w-full overflow-hidden" data-testid="hero-video-section">
          <div className="relative w-full" style={{ aspectRatio: "21 / 9" }}>
            <iframe
              src={(() => {
                const u = settings.hero_video_url;
                const yt1 = u.match(/youtube\.com\/watch\?v=([\w-]+)/);
                const yt2 = u.match(/youtu\.be\/([\w-]+)/);
                const vim = u.match(/vimeo\.com\/(\d+)/);
                if (yt1) return `https://www.youtube.com/embed/${yt1[1]}?autoplay=1&mute=1&loop=1&playlist=${yt1[1]}&controls=0&showinfo=0&modestbranding=1`;
                if (yt2) return `https://www.youtube.com/embed/${yt2[1]}?autoplay=1&mute=1&loop=1&playlist=${yt2[1]}&controls=0&showinfo=0&modestbranding=1`;
                if (vim) return `https://player.vimeo.com/video/${vim[1]}?autoplay=1&muted=1&loop=1&background=1`;
                return u;
              })()}
              title="Hero"
              className="absolute inset-0 h-full w-full border-0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#FBFBF9] via-transparent to-transparent" />
          </div>
        </section>
      )}
      <section className="relative overflow-hidden">
        <div className="container-cnp grid lg:grid-cols-12 gap-10 pt-14 lg:pt-24 pb-16 lg:pb-24 items-center">
          <div className="lg:col-span-6 fade-up">
            <span className="tag-chip">Porto · Gondomar · Maia · Ermesinde · Valongo</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05]">
              Encontre a casa<br/>
              dos seus sonhos<br/>
              <span className="italic text-[#103C4A]">e transforme-a no seu lar.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-stone-600 leading-relaxed">
              Sou a <strong className="text-stone-900 font-medium">Zélia Silva</strong>, consultora imobiliária no Grande Porto,
              com mais de <strong className="text-stone-900 font-medium">10 anos de experiência</strong>. Acompanho-o do primeiro café à escritura,
              com escuta, verdade e resultado.
            </p>
            <div className="mt-6">
              <TrustBadge compact />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/vender-casa-no-porto" data-testid="hero-cta-vender" className="btn-primary">
                Quero vender a minha casa <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/comprar-casa-no-porto" data-testid="hero-cta-comprar" className="btn-secondary">
                Quero comprar casa
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-widest text-stone-500">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (<Star key={i} className="h-3.5 w-3.5 fill-[#103C4A] text-[#103C4A]" />))}
                <span className="ml-2 normal-case tracking-normal text-stone-600">Clientes 5 estrelas</span>
              </div>
              <div>eXp Portugal</div>
            </div>
          </div>

          <div className="lg:col-span-6 fade-up fade-up-delay-2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-[#103C4A]/5 blur-2xl" aria-hidden />
              <div className="relative grid grid-cols-6 grid-rows-6 gap-3 h-[480px] md:h-[560px]">
                <div className="col-span-4 row-span-4 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(16,60,74,0.15)]">
                  <img src="https://images.pexels.com/photos/34653027/pexels-photo-34653027.jpeg" alt="Porto" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-lg">
                  <img src={CONTACT.photo} alt="Zélia Silva" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-lg">
                  <img src="https://ywzpnbmomlzkcbzzkaqr.supabase.co/storage/v1/object/public/listing-images/PT/86491282/listings/e5dc59d9da40671b84a88ebb6c761ff6.jpg" alt="Imóvel Rio Tinto" className="h-full w-full object-cover" />
                </div>
                <div className="col-span-4 row-span-2 rounded-2xl overflow-hidden shadow-lg">
                  <img src="https://ywzpnbmomlzkcbzzkaqr.supabase.co/storage/v1/object/public/listing-images/PT/86827403/listings/4e5e9c7c4f866c62d3b6121fb28edf2a.jpg" alt="Imóvel Areosa" className="h-full w-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO ENTRIES */}
      <section className="container-cnp py-16 lg:py-20">
        <div className="grid md:grid-cols-2 gap-6">
          <Link data-testid="entry-vender" to="/vender-casa-no-porto" className="group relative overflow-hidden rounded-3xl bg-[#103C4A] p-10 text-white transition-transform duration-500 hover:-translate-y-1">
            <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/5" />
            <span className="text-xs uppercase tracking-[0.2em] opacity-80">Para vendedores</span>
            <h3 className="mt-4 font-display text-3xl lg:text-4xl leading-tight">Vender casa no Porto<br/><span className="italic opacity-80">com estratégia e serenidade.</span></h3>
            <p className="mt-4 max-w-md opacity-80">Avaliação realista, marketing profissional, negociação humana. Uma venda bem feita começa antes do anúncio.</p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm">Ver como trabalho <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
          </Link>
          <Link data-testid="entry-comprar" to="/comprar-casa-no-porto" className="group relative overflow-hidden rounded-3xl bg-white border border-stone-200 p-10 text-stone-900 transition-transform duration-500 hover:-translate-y-1">
            <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Para compradores</span>
            <h3 className="mt-4 font-display text-3xl lg:text-4xl leading-tight text-stone-900">Comprar casa no Porto<br/><span className="italic text-[#103C4A]">com tempo e critério.</span></h3>
            <p className="mt-4 max-w-md text-stone-600">Da reunião ao acompanhamento bancário. Só apresento o que faz sentido para si.</p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#103C4A]">Como funciona <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
          </Link>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="container-cnp py-16 lg:py-20">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <span className="tag-chip">Imóveis em destaque</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Uma seleção recente, escolhida com cuidado.</h2>
          </div>
          <Link to="/imoveis" data-testid="link-all-properties" className="hidden md:inline-flex btn-ghost">Ver todos <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((p) => (<PropertyCard key={p.id} property={p} />))}
        </div>
        <div className="mt-8 md:hidden">
          <Link to="/imoveis" data-testid="link-all-properties-mobile" className="btn-secondary">Ver todos os imóveis</Link>
        </div>
      </section>

      {/* ZONES BLOCK */}
      <section className="bg-white border-y border-stone-200/70">
        <div className="container-cnp py-16 lg:py-20">
          <div className="max-w-2xl">
            <span className="tag-chip">Zonas onde atuo</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Um consultor para a sua zona faz toda a diferença.</h2>
            <p className="mt-4 text-stone-600">Conheço bem o Porto, Maia, Ermesinde e Valongo. Ruas, escolas, preço médio, procura real.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {zones.map((z) => (
              <Link key={z.id} data-testid={`zone-card-${z.slug}`} to={`/zonas/${z.slug}`} className="group relative overflow-hidden rounded-2xl aspect-[4/5] block">
                <img src={toImg(z.main_image)} alt={z.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest opacity-80"><MapPin className="h-3.5 w-3.5" /> Grande Porto</div>
                  <div className="mt-1 font-display text-2xl">{z.name}</div>
                  <div className="mt-1 text-sm opacity-80">{z.short_intro}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-cnp py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <span className="tag-chip">Serviços</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Tudo o que precisa, num só interlocutor.</h2>
            <p className="mt-4 max-w-lg text-stone-600">Da avaliação à escritura, um serviço integrado, com parceiros de confiança em crédito, fiscalidade e obras.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: TrendingUp, title: "Avaliação de imóvel", desc: "Análise de mercado local, com relatório claro e sem compromisso." },
              { icon: HomeIcon, title: "Home staging leve", desc: "Preparação da casa e fotografia profissional para valorizar o anúncio." },
              { icon: Sparkles, title: "Marketing digital", desc: "Portais, redes sociais e base de compradores qualificados." },
              { icon: HeartHandshake, title: "Acompanhamento", desc: "Do primeiro contacto à escritura, sem que se sinta sozinho." },
            ].map((s, i) => (
              <div key={i} data-testid={`service-card-${i}`} className="card-soft p-6">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#103C4A]/10 text-[#103C4A]">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg text-stone-900">{s.title}</h3>
                <p className="mt-2 text-sm text-stone-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ZELIA TRUST SECTION */}
      <section className="bg-white border-y border-stone-200/70">
        <div className="container-cnp py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-[#103C4A]/5" aria-hidden />
              <img src={CONTACT.photo} alt="Zélia Silva" className="relative rounded-3xl object-cover h-[520px] w-full" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="tag-chip">Sobre a Zélia</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900 leading-tight">10+ anos a maximizar o valor e a experiência dos meus clientes.</h2>
            <p className="mt-6 text-stone-700 leading-relaxed">
              Sou uma profissional do setor imobiliário com mais de 10 anos de experiência dedicados a
              maximizar o valor e a experiência para os meus clientes — sejam eles compradores ou vendedores.
            </p>
            <p className="mt-4 text-stone-700 leading-relaxed">
              O meu objetivo é simples: ajudá-lo a concretizar o seu objetivo, seja ele vender ou comprar
              um imóvel. Trabalho como consultora associada à <strong>eXp Portugal</strong>, com uma rede
              internacional que me permite oferecer um serviço completo.
            </p>
            <div className="mt-6">
              <TrustBadge />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/sobre" data-testid="link-sobre-zelia" className="btn-primary">Conhecer o meu método</Link>
              <Link to="/contacto" data-testid="link-contacto-zelia" className="btn-secondary">Marcar reunião</Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-cnp py-16 lg:py-20">
        <span className="tag-chip">Clientes</span>
        <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">O que dizem quem já cá passou.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`testimonial-${t.id}`} className="card-soft p-8 relative">
              <Quote className="h-6 w-6 text-[#103C4A]/40" />
              <p className="mt-4 text-stone-700 leading-relaxed">&ldquo;{t.testimonial}&rdquo;</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#103C4A]/10 text-[#103C4A] flex items-center justify-center font-medium">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-medium text-stone-900">{t.name}</div>
                  <div className="text-xs text-stone-500 capitalize">{t.client_type} · {t.zone}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="container-cnp py-16 lg:py-20">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <span className="tag-chip">Blog</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Ideias e guias sobre o mercado no Porto.</h2>
          </div>
          <Link to="/blog" data-testid="link-all-blog" className="hidden md:inline-flex btn-ghost">Ver todos <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group card-soft overflow-hidden">
              <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                <img src={toImg(p.main_image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
              </div>
              <div className="p-6">
                <span className="tag-chip">{p.category}</span>
                <h3 className="mt-3 text-lg text-stone-900 leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-stone-600 line-clamp-2">{p.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-white border-t border-stone-200">
        <div className="container-cnp py-16 lg:py-24 grid lg:grid-cols-2 gap-14">
          <div>
            <span className="tag-chip">Falar consigo</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-stone-900">Comece por uma reunião. Sem compromisso.</h2>
            <p className="mt-4 max-w-md text-stone-600">Escreva-me com o que precisa e eu respondo no próprio dia útil, com clareza.</p>
            <div className="mt-8 space-y-4 text-stone-700">
              <div>📞 <a href={`tel:${CONTACT.phoneRaw}`} className="link-underline">{CONTACT.phone}</a> · <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer" className="link-underline">WhatsApp</a></div>
              <div>✉️ <a href={`mailto:${CONTACT.email}`} className="link-underline">{CONTACT.email}</a></div>
              <div>📍 Porto e Grande Porto</div>
            </div>
          </div>
          <div>
            <ContactForm testidPrefix="home-contact" />
          </div>
        </div>
      </section>
    </div>
  );
}
